/**
 * Allow-list sanitizer for inbound email HTML (#363).
 *
 * Parses with `DOMParser` and walks the resulting tree rather than pattern-matching
 * the markup. Regex-based sanitizers leak because they disagree with the browser's
 * own parser about what ambiguous markup means; letting the browser parse removes
 * that entire class of bypass. `parseFromString` builds an INERT document — no
 * script runs and no image loads while parsing — so this is side-effect-free.
 *
 * Layering, stated precisely because the boundary is narrower than it looks. The
 * `sandbox` on the rendering iframe (no `allow-same-origin`) is the first layer: it
 * gives the frame an opaque origin, so SCRIPT running inside cannot read the app's
 * storage, cookies or DOM. What it does NOT contain is URL resolution — a `srcdoc`
 * document resolves relative references against its PARENT's URL, so a relative
 * `href` still points at the real origin. That gap is closed HERE, by requiring an
 * absolute allow-listed scheme on every URL attribute (see `isSafeUrl`). Treat the
 * two layers as covering different things rather than one backstopping the other.
 *
 * Unlisted elements are removed TOGETHER WITH THEIR CONTENTS, never unwrapped: a tag
 * we refuse to render almost always holds content that isn't meant to be read as text
 * either, and unwrapping dumps that into the message as garbage. The allow-list is
 * therefore deliberately broad, and `sanitizeEmailHtml` returning empty is a
 * meaningful signal to the caller to fall back to plain text.
 *
 * Design: docs/superpowers/specs/2026-07-27-email-html-rendering-design.md
 */

/** Elements rendered as-is. Anything absent here is removed with its subtree. */
const ALLOWED_TAGS = new Set([
	// Sectioning and structure
	'div', 'span', 'p', 'br', 'hr', 'center', 'section', 'article', 'main',
	'header', 'footer', 'nav', 'aside', 'figure', 'figcaption', 'address',
	'details', 'summary', 'hgroup',
	// Headings
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
	// Text and inline
	'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'small', 'big',
	'tt', 'nobr', 'sub', 'sup', 'code', 'pre', 'samp', 'kbd', 'var', 'blockquote',
	'q', 'cite', 'abbr', 'acronym', 'mark', 'time', 'data', 'dfn', 'bdi', 'bdo',
	'ruby', 'rt', 'rp', 'wbr', 'font',
	// Lists
	'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'menu',
	// Tables
	'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
	// Links and media
	'a', 'img', 'picture', 'source',
	// Layout CSS — scrubbed below; Outlook forwards depend on it
	'style',
]);

/** Attributes allowed on any permitted element. `on*` handlers are absent by design. */
const GLOBAL_ATTRS = new Set([
	'style', 'class', 'title', 'dir', 'lang', 'align', 'valign', 'width', 'height',
	'bgcolor', 'color', 'face', 'size', 'border', 'cellpadding', 'cellspacing',
	'colspan', 'rowspan',
]);

/** Additional attributes allowed only on specific elements. */
const TAG_ATTRS: Record<string, Set<string>> = {
	a: new Set(['href', 'target', 'rel']),
	img: new Set(['src', 'alt']),
	source: new Set(['srcset', 'type', 'media']),
	time: new Set(['datetime']),
	td: new Set(['headers', 'scope']),
	th: new Set(['headers', 'scope']),
};

/** Attributes whose value is a URL and must pass scheme validation. */
const URL_ATTRS = new Set(['href', 'src', 'srcset']);

const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Characters browsers ignore inside a URL, and which therefore cannot be trusted
 * to separate a scheme. Matching control characters is the entire point here — an
 * attacker writes `java\tscript:` precisely because a naive check will not see it.
 */
// eslint-disable-next-line no-control-regex -- control characters are the payload being defused
const URL_NOISE = /[\u0000-\u0020\u007F-\u00A0]/g;

/**
 * Reduce a URL to the form the BROWSER will act on before testing its scheme.
 * Browsers ignore leading whitespace and strip embedded control characters, so
 * `java\tscript:` and `  JaVaScRiPt:` both navigate. Entity forms like
 * `java&#115;cript:` are already decoded for us by the parser.
 */
const schemeOf = (value: string): string | null => {
	const match = value.replace(URL_NOISE, '').match(/^([a-z][a-z0-9+.-]*):/i);
	return match ? `${match[1].toLowerCase()}:` : null;
};

/**
 * A URL is safe only when it carries an explicitly allowed scheme.
 *
 * Relative and protocol-relative URLs are REJECTED. It is tempting to treat them
 * as harmless because the frame has an opaque origin, but that is wrong: a
 * `srcdoc` document resolves relative references against its PARENT document's
 * URL. Sandboxing changes the frame's origin, not the URL-resolution algorithm.
 * Verified in Chromium with this exact sandbox string — `href="/some/path"`
 * resolved to the real app origin while `window.origin` reported `"null"`.
 *
 * That matters because links are forced to `target="_blank"` and the sandbox
 * carries `allow-popups-to-escape-sandbox`, so the popup is NOT sandboxed: a
 * click would open a real, fully authenticated tab on the app's own origin.
 * `//evil.com/x` is the same hole pointing outward — no scheme to check, then
 * resolved against the parent's scheme.
 *
 * Real email uses absolute URLs (it has to — it renders in arbitrary clients),
 * so nothing legitimate is lost.
 */
export const isSafeUrl = (value: string, allowDataImage = false): boolean => {
	const scheme = schemeOf(value);
	if (!scheme) return false;
	if (scheme === 'data:') {
		return allowDataImage && /^data:image\//i.test(value.replace(URL_NOISE, ''));
	}
	return SAFE_SCHEMES.has(scheme);
};

/**
 * `srcset` is a comma-separated list of `url descriptor` candidates, so checking
 * it as a single URL only ever validates the head of the string — a later
 * candidate could carry any scheme at all. Every candidate is validated, and the
 * whole attribute is dropped if any fails (a URL containing a literal comma
 * fails closed here, which is fine: `<picture>` still renders its `<img>`).
 */
const isSafeSrcset = (value: string): boolean =>
	value.split(',').every((candidate) => {
		const url = candidate.trim().split(/\s+/)[0];
		return !url || isSafeUrl(url, true);
	});

/**
 * Remove the CSS constructs that fetch or execute: `@import` pulls a remote
 * stylesheet, legacy IE `expression()` runs script, and `url()` can carry a
 * `javascript:` payload. Ordinary declarations are left untouched.
 */
const scrubCss = (css: string): string =>
	css
		.replace(/@import[^;]*;?/gi, '')
		.replace(/expression\s*\([^)]*\)/gi, '')
		.replace(/url\(\s*['"]?\s*(?!https?:|data:image\/|\/\/|['")])[^)]*\)/gi, '');

/** Filter one element's attributes down to the allow-list. */
function sanitizeAttributes(el: Element, tag: string): void {
	const allowed = TAG_ATTRS[tag];
	for (const attr of [...el.attributes]) {
		const name = attr.name.toLowerCase();
		if (!GLOBAL_ATTRS.has(name) && !allowed?.has(name)) {
			el.removeAttribute(attr.name);
			continue;
		}
		if (URL_ATTRS.has(name)) {
			const safe = name === 'srcset'
				? isSafeSrcset(attr.value)
				: isSafeUrl(attr.value, tag === 'img' || tag === 'source');
			if (!safe) {
				// Drop the attribute, not the element — the link text stays readable.
				el.removeAttribute(attr.name);
				continue;
			}
		}
		if (name === 'style') el.setAttribute('style', scrubCss(attr.value));
	}
}

/** Recursively filter `node`'s children in place. */
function sanitizeChildren(node: Element, allowStyleElements: boolean): void {
	for (const child of [...node.childNodes]) {
		if (child.nodeType === 8 /* COMMENT_NODE */) {
			child.parentNode?.removeChild(child);
			continue;
		}
		if (child.nodeType !== 1 /* ELEMENT_NODE */) continue;

		const el = child as Element;
		const tag = el.tagName.toLowerCase();
		if (!ALLOWED_TAGS.has(tag)) {
			el.remove();
			continue;
		}
		if (tag === 'style' && !allowStyleElements) {
			el.remove();
			continue;
		}
		sanitizeAttributes(el, tag);
		if (tag === 'style') {
			el.textContent = scrubCss(el.textContent ?? '');
			continue;
		}
		if (tag === 'a' && el.getAttribute('href')) {
			el.setAttribute('target', '_blank');
			el.setAttribute('rel', 'noopener noreferrer');
		}
		sanitizeChildren(el, allowStyleElements);
	}
}

export interface SanitizeEmailHtmlOptions {
	/**
	 * Keep `<style>` blocks (the default) or strip them.
	 *
	 * Set FALSE when the result is injected into one of our own documents rather than a
	 * sandboxed iframe — currently only the print page (#465), which cannot use an iframe
	 * because Chrome clips iframe content to a single page instead of paginating it.
	 *
	 * The concern is not script; the allow-list already handles that. It is SCOPE. Inside
	 * a `srcdoc` frame a stranger's document-level CSS can only affect their own document.
	 * In ours it applies to the whole page, so a `body{display:none}` buried in a
	 * forwarded Outlook signature would print a blank sheet with nothing to explain it.
	 *
	 * Inline `style` ATTRIBUTES survive either way — they cannot escape their element, and
	 * they are what most email formatting is actually made of.
	 */
	allowStyleElements?: boolean;
}

/**
 * Apply the allow-list to an email body fragment or full document.
 * Returns '' when nothing renderable survives — callers should fall back to
 * the plain-text body rather than showing a blank pane.
 */
export function sanitizeEmailHtml(
	html: string | null | undefined,
	options: SanitizeEmailHtmlOptions = {},
): string {
	if (!html || !html.trim()) return '';
	const allowStyleElements = options.allowStyleElements !== false;

	const doc = new DOMParser().parseFromString(html, 'text/html');
	if (!doc.body) return '';

	// A full-document email keeps its layout CSS in <head>; a body-only walk would
	// silently discard exactly the styles the forward depends on. Skipped entirely in
	// print mode — see SanitizeEmailHtmlOptions.
	const headStyles = allowStyleElements
		? [...(doc.head?.querySelectorAll('style') ?? [])]
			.map((el) => `<style>${scrubCss(el.textContent ?? '')}</style>`)
			.join('')
		: '';

	// html/head/body are document skeleton, not content — never allow-list-checked.
	sanitizeChildren(doc.body, allowStyleElements);

	return `${headStyles}${doc.body.innerHTML}`;
}
